import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import en from './en';
import it from './it';
import hi from './hi';

const DICTIONARIES = { en, it, hi };
const DEFAULT_LANG = 'en';

const detectLang = () => {
  try {
    const stored = localStorage.getItem('hipro_lang');
    if (stored && DICTIONARIES[stored]) return stored;
    const nav = (navigator.language || navigator.userLanguage || '').toLowerCase();
    if (nav.startsWith('it')) return 'it';
    if (nav.startsWith('hi')) return 'hi';
    return DEFAULT_LANG;
  } catch (e) {
    return DEFAULT_LANG;
  }
};

const getPath = (obj, path) => {
  const parts = path.split('.');
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
};

const I18nContext = createContext({ lang: DEFAULT_LANG, setLang: () => {}, t: (k) => k });

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(DEFAULT_LANG);

  useEffect(() => {
    setLangState(detectLang());
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((code) => {
    if (!DICTIONARIES[code]) return;
    setLangState(code);
    try { localStorage.setItem('hipro_lang', code); } catch (e) { /* ignore */ }
  }, []);

  const t = useCallback((key, fallback) => {
    const dict = DICTIONARIES[lang] || DICTIONARIES[DEFAULT_LANG];
    const val = getPath(dict, key);
    if (val !== undefined) return val;
    // fallback to English
    const enVal = getPath(DICTIONARIES[DEFAULT_LANG], key);
    if (enVal !== undefined) return enVal;
    return fallback !== undefined ? fallback : key;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t, languages: Object.keys(DICTIONARIES).map((k) => DICTIONARIES[k].meta) }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);

export default I18nContext;
